import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Clock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FallingMapleLeaves } from '@/components/shared/FallingMapleLeaves';

const EventsPage = () => {
  return (
    <main data-testid="events-page" className="overflow-hidden">
      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0 -top-20">
          <img src="https://images.unsplash.com/photo-1601655856683-8400aa9ef59f?w=1920&q=80" alt="Canvas" className="w-full h-full object-cover" style={{ filter: 'saturate(0.6) brightness(1.1) contrast(0.95) sepia(0.15)' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white" />
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-green/8 via-transparent to-brand-brown/12" />
        </div>
        <FallingMapleLeaves />
        <div className="w-full pl-6 md:pl-12 lg:pl-24 pr-6 relative z-10">
          <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-green/20 mb-6">
              <Sparkles className="w-4 h-4 text-brand-green" />
              <span className="font-sans text-sm font-semibold text-brand-green uppercase tracking-wider">Events</span>
            </motion.div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-dark mb-5 leading-[1.1]">
              Upcoming <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Events</span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="font-sans text-lg md:text-xl text-gray-700 leading-relaxed max-w-xl">
              Training programs and community events at the intersection of carbon markets, compliance, and climate action.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Event Cards */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {/* CCTS Training Card */}
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="group">
              <Link to="/events/ccts-training">
                <div className="relative bg-gradient-to-br from-brand-surface to-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-brand-green/10 transition-all duration-500">
                  <div className="relative h-56 overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80" alt="CCTS Training" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-green/90 text-white text-xs font-semibold shadow-lg">Training Program</span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg">
                        <div className="flex items-center gap-4 text-xs text-gray-600">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> 12-13 Sep 2026</span>
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> 2 Days</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Zoom</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl text-brand-dark mb-2">CCTS Mastery</h3>
                    <p className="font-sans text-sm text-gray-600 mb-4">A compliance-to-market training program for obligated entities and carbon market professionals. 8 modules, 6 hours, certificate of completion.</p>
                    <div className="flex items-center justify-between">
                      <span className="font-sans text-sm font-semibold text-brand-green">Early Bird: ₹3,499</span>
                      <span className="flex items-center gap-1 font-sans text-sm text-brand-brown group-hover:translate-x-1 transition-transform">Learn More <ArrowRight className="w-4 h-4" /></span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Carbon Circular Live Card */}
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="group">
              <Link to="/events/carbon-circular-live">
                <div className="relative bg-gradient-to-br from-brand-surface to-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-brand-brown/10 transition-all duration-500">
                  <div className="relative h-56 overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&q=80" alt="Carbon Circular Live" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-brown/90 text-white text-xs font-semibold shadow-lg">Community Event</span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg">
                        <div className="flex items-center gap-4 text-xs text-gray-600">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Jul 17 & Sep 26</span>
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> 1 Hour</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Delhi</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl text-brand-dark mb-2">Carbon Circular Live</h3>
                    <p className="font-sans text-sm text-gray-600 mb-4">The offline edition. An informal hour of what's moving, what's breaking, and what everyone's actually thinking — but not posting about.</p>
                    <div className="flex items-center justify-between">
                      <span className="font-sans text-sm font-semibold text-brand-brown">Blue Tokai, Janpath</span>
                      <span className="flex items-center gap-1 font-sans text-sm text-brand-brown group-hover:translate-x-1 transition-transform">Learn More <ArrowRight className="w-4 h-4" /></span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 relative overflow-hidden bg-gradient-to-br from-brand-surface via-white to-brand-surface">
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-dark leading-tight mb-4">Want a <span className="italic text-brand-green">custom training</span> for your team?</h2>
            <p className="font-sans text-base text-gray-600 mb-8 max-w-2xl mx-auto">We design bespoke programs tailored to your sector, compliance exposure, and team level.</p>
            <Link to="/contact">
              <Button className="group bg-gradient-to-r from-brand-green to-emerald-500 text-white px-8 py-6 rounded-full text-base font-semibold shadow-xl shadow-brand-green/20 hover:shadow-brand-green/40 transition-all">
                Get in Touch <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default EventsPage;
